import TableOnly from "components/DataTable/TableOnly";
import PaginationWithState from "components/Pagination/PaginationWithState";
import LossRunReportButton from "components/Pdfs/LossRunReport/LossRunReportButton";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import DeleteModalButton from "../../../../components/DeleteModalBtn/DeleteModalButton";
import { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import { convertSqlTimestampToDate, truncateString } from "utils/UtilityFuncs";
import * as XLSX from "xlsx";

export default function LossRunReport() {
  const dispatch = useDispatch();
  /*   ALL STATES
   ********************************************* */
  const [filterBy, setFilterBy] = useState({
    name: "Filter by All",
    value: null,
  });
  const [lossRunReportData, setLossRunReportData] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({});
  const [isUpdateMode, setIsUpdateMode] = useState(false);

  const [loading, setLoading] = useState(false);

  // Export Modal states
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportStartDate, setExportStartDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() - 1, 2)
      .toISOString()
      .slice(0, 10);
  });
  const [exportEndDate, setExportEndDate] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [exporting, setExporting] = useState(false);

  // Pagination/Search states
  const [pageLimit, setPageLimit] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() - 1, 2)
      .toISOString()
      .slice(0, 10)
      .replace("T", " ");
  });

  const [endDate, setEndDate] = useState(
    new Date().toISOString().slice(0, 10).replace("T", " "),
  );

  /*   All Functions
   ********************************************* */
  // Export to Excel function
  const handleExportToExcel = async (e) => {
    e.preventDefault();
    try {
      setExporting(true);
      const { data } = await API.post(
        "/manufacturer/loss-run-reports/export",
        {
          startDate: new Date(exportStartDate + "T00:00:00").toISOString(),
          endDate: new Date(exportEndDate + "T23:59:59").toISOString(),
        },
        { headers: authHeader() },
      );

      if (data?.success && data?.data) {
        // Check if data is empty
        if (data.data.length === 0) {
          dispatch(
            setNotification({
              message: "No results found for the selected date range",
              type: "info",
            }),
          );
          return;
        }

        const exportData = data.data.map((item) => ({
          "L-ID": `L-${item?.lossId}`,
          "Created At": convertSqlTimestampToDate(item?.createdAt, true),
          "By Employee": item?.byEmployee || "",
          Department: item?.department || "",
          Amount: item?.amount || "",
          Description: item?.description || "", // Full description
          "Last Updated": convertSqlTimestampToDate(item?.updatedAt, true),
          "Last Print Date": item?.printDate
            ? convertSqlTimestampToDate(item?.printDate, true)
            : "-",
          "Printed By": item?.printBy || "-",
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Loss Run Report");

        // Generate filename with date range
        const filename = `LossRunReport_${exportStartDate}_to_${exportEndDate}.xlsx`;
        XLSX.writeFile(workbook, filename);

        setShowExportModal(false);
        dispatch(
          setNotification({
            message: "Export successful",
            type: "success",
          }),
        );
      } else {
        dispatch(
          setNotification({
            message: data?.message || "Failed to export",
            type: "error",
          }),
        );
      }
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setExporting(false);
    }
  };

  //get Data (uses POST /manufacturer/loss-run-reports?page=&limit=)
  const getLossRunReportData = useCallback(
    async ({ clearSearch = false, query, page } = {}) => {
      try {
        setLoading(true);
        const usedQuery = clearSearch
          ? ""
          : typeof query !== "undefined"
            ? query
            : "";
        const usedPage = page ?? pageNumber;
        const { data } = await API.post(
          "/manufacturer/loss-run-reports?page=" +
            usedPage +
            "&limit=" +
            pageLimit,
          {
            query: usedQuery,
            filterBy:
              usedQuery && usedQuery !== "" ? filterBy.value : undefined,
          },
          { headers: authHeader() },
        );

        if (data?.success) {
          // API returns { success, data, total, page, limit, totalPages }
          setLossRunReportData(data?.data || []);
          setTotalPages(data?.totalPages || 0);
        } else {
          dispatch(
            setNotification({
              message: data?.message || "Failed to fetch",
              type: "error",
            }),
          );
        }
      } catch (err) {
        dispatch(setNotification({ message: err.message, type: "error" }));
      } finally {
        setLoading(false);
      }
    },
    [pageNumber, pageLimit, filterBy.value, dispatch],
  );

  // Delete LossRunReport
  const deleteLossRunReportData = async (id) => {
    try {
      setLoading(true);
      const { data } = await API.delete("/manufacturer/loss-run-report/" + id, {
        headers: authHeader(),
      });
      if (data?.success) {
        // refresh current page
        getLossRunReportData();
        dispatch(
          setNotification({
            message: data?.message || "Deleted",
            type: "success",
          }),
        );
        return;
      }
      dispatch(
        setNotification({
          message: data?.message || "Failed to delete",
          type: "error",
        }),
      );
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setLoading(false);
    }
  };

  // Adds Button Function
  const handleLossRunReportData = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = { ...formData };
      const { data } = await API.post(
        "/manufacturer/loss-run-report",
        payload,
        {
          headers: authHeader(),
        },
      );
      if (data?.success) {
        // after add refresh current page (or go to first page)
        setFormData({});
        setShowAddModal(false);
        // Refresh list
        setPageNumber(1);
        getLossRunReportData({ page: 1 });
      } else {
        dispatch(setNotification({ message: data?.message, type: "error" }));
      }
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setLoading(false);
    }

    setShowAddModal(false);
  };

  // Update Button Function
  const handleUpdateLossRunReportData = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = { ...formData };
      const { data } = await API.patch(
        "/manufacturer/loss-run-report",
        payload,
        {
          headers: authHeader(),
        },
      );
      if (data?.success) {
        setIsUpdateMode(false);
        setShowAddModal(false);
        // refresh list
        getLossRunReportData();
      } else {
        dispatch(setNotification({ message: data?.message, type: "error" }));
      }
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateData = async (item) => {
    // populate formData with expected fields
    setFormData({
      id: item.id,
      lossId: item.lossId,
      byEmployee: item?.byEmployee,
      department: item?.department,
      attendanceUserName: item?.attendanceUser?.name || "",
      amount: item.amount || "",
      description: item.description || "",
    });
    setIsUpdateMode(true);
    setShowAddModal(true);
  };

  const handlePrintDate = async (item) => {
    try {
      const { data } = await API.post(
        "/manufacturer/update-loss-run-report-print-date",
        { id: item.id },
        { headers: authHeader() },
      );
      if (data?.success) {
        let updatedItem = data?.data;
        setLossRunReportData((prev) => {
          let newData = [...prev];
          let index = newData.findIndex((d) => d.id === updatedItem.id);
          if (index !== -1) {
            newData[index] = { ...newData[index], ...updatedItem };
          }
          return newData;
        });
      }
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
      console.log(err.message);
    }
  };

  // Quick filter function for export modal
  const handleQuickFilter = (filter) => {
    const now = new Date();
    let start,
      end = now;

    switch (filter) {
      case "today":
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case "1week":
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
        break;
      case "thisMonth":
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case "3months":
        start = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
        break;
      case "6months":
        start = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
        break;
      case "thisYear":
        start = new Date(now.getFullYear(), 0, 1);
        break;
      default:
        return;
    }

    setExportStartDate(start.toISOString().slice(0, 10));
    setExportEndDate(end.toISOString().slice(0, 10));
  };

  useEffect(() => {
    let timeout = setTimeout(() => {
      getLossRunReportData();
    }, 50);
    return () => clearTimeout(timeout);
  }, [getLossRunReportData]);

  /*   Table functions
   ********************************************* */
  const buttons = () => <div className=" d-flex justify-content-end "></div>;

  const table_head = [
    { name: "L-ID", value: "lossId" },
    { name: "Created At", value: "creationAt" },
    { name: "By Employee", value: "byEmployee" },
    { name: "Department", value: "department" },
    { name: "Amount", value: "amount" },
    { name: "Description", value: "description" },
    { name: "Last Updated", value: "updatedAt" },
    { name: "Last Print Date", value: "printDate" },
    { name: "Printed By", value: "printBy" },

    { name: "Actions", value: "Actions" },
  ];

  const table_body = (item, index) => (
    <tr className="" key={item.id}>
      <td className="align-middle py-1 text-nowrap">L-{item?.lossId}</td>
      <td className="align-middle py-1">
        {convertSqlTimestampToDate(item?.createdAt, true)}
      </td>
      <td className="align-middle py-1 text-truncate">{item?.byEmployee}</td>
      <td className="align-middle py-1 text-truncate">{item?.department}</td>
      <td className="align-middle py-1">{item?.amount}</td>
      <td className="align-middle py-1 text-truncate">
        {truncateString(item?.description, 50)}
      </td>
      <td className="align-middle py-1">
        {convertSqlTimestampToDate(item?.updatedAt, true)}
      </td>
      <td className="align-middle py-1">
        {item?.printDate
          ? convertSqlTimestampToDate(item?.printDate, true)
          : "-"}
      </td>
      <td className="align-middle py-1">
        {item?.printBy ? item?.printBy : "-"}
      </td>
      <td className="align-middle py-1 d-flex align-items-center justify-content-end">
        <LossRunReportButton
          reportData={item}
          handlePrintDate={handlePrintDate}
        />
        <button
          disabled={loading}
          onClick={() => {
            handleUpdateData(item);
          }}
          type="button"
          className="btn btn-sm px-1"
        >
          <i className="mdi mdi-pencil"></i>
        </button>

        <DeleteModalButton
          btnClass={"btn btn-sm  px-1"}
          deleteFunc={deleteLossRunReportData}
          deleteParams={item.id}
          disableConditions={loading}
        />
      </td>
    </tr>
  );

  return (
    <div className="p-2" style={{ marginLeft: 260, height: "100vh" }}>
      <h2 className="mb-3 ps-2">Loss Run Report</h2>
      {loading && <Spinner />}
      {/*   Table
       ********************************************* */}
      <div className="d-flex justify-content-between mb-3 align-items-center">
        <div className="px-2">
          Show &nbsp;
          <div className="btn-group">
            <button
              className="px-1 bg-primary bg-opacity-10 border border-primary rounded-end rounded-start"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              {pageLimit}
            </button>

            <ul className="dropdown-menu w-25">
              <li onClick={() => setPageLimit(5)}>
                <button className="dropdown-item">5</button>
              </li>
              <li onClick={() => setPageLimit(10)}>
                <button className="dropdown-item">10</button>
              </li>
              <li onClick={() => setPageLimit(15)}>
                <button className="dropdown-item">15</button>
              </li>
              <li onClick={() => setPageLimit(20)}>
                <button className="dropdown-item">20</button>
              </li>
            </ul>
          </div>
        </div>

        <div className="d-flex">
          <button
            onClick={() => {
              setShowExportModal(true);
            }}
            type="button"
            className="btn btn-success ms-1"
          >
            Export
          </button>
          <div className="me-1"> {buttons()}</div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setPageNumber(1);
              if (searchTerm && searchTerm !== "") {
                getLossRunReportData({ query: searchTerm, page: 1 });
              } else {
                getLossRunReportData({ clearSearch: true, page: 1 });
              }
            }}
            className="d-flex justify-content-end gap-1 align-items-center"
          >
            <input
              type="search"
              placeholder="Search by name..."
              value={searchTerm}
              className="form-control"
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button className="btn btn-info" type="submit">
              Search
            </button>
            {searchTerm !== "" && (
              <button
                className="btn btn-outline-danger"
                type="button"
                onClick={() => {
                  setSearchTerm("");
                  getLossRunReportData({ clearSearch: true, page: 1 });
                }}
              >
                Clear
              </button>
            )}
          </form>
          {/* Add button moved next to search for consistent layout */}
          <div className="d-flex">
            <button
              onClick={() => {
                setShowAddModal(true);
                setFormData({});
                setIsUpdateMode(false);
              }}
              type="button"
              className="btn btn-primary ms-1"
            >
              Add Loss
            </button>
          </div>
        </div>
      </div>

      <div
        className="px-1 flex-grow-1 overflow-y-auto d-flex"
        style={{ height: "78vh", borderRadius: 15 }}
      >
        <div className="horizontal-scrollable d-flex flex-grow-1">
          <TableOnly
            table_head={table_head}
            table_body={table_body}
            table_data={lossRunReportData}
            disableSorting={false}
            actionsOnEnd={true}
          />
        </div>
      </div>

      <div className="d-flex justify-content-end mt-2">
        <PaginationWithState
          totalPages={totalPages}
          currPage={pageNumber}
          setCurrPage={setPageNumber}
        />
      </div>

      {/*   Add Modal
       ********************************************* */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
        <Modal.Body
          className="shadow-lg bg-white px-3 "
          style={{ borderRadius: 15 }}
        >
          <div className="">
            <h4>{isUpdateMode ? "Update Loss" : "Add Loss"}</h4>
          </div>
          <hr className="mt-0" />
          <form
            onSubmit={
              isUpdateMode
                ? handleUpdateLossRunReportData
                : handleLossRunReportData
            }
            className=""
          >
            <div className="mb-1">
              <label className="">By Employee</label>
              <div className="">
                <input
                  type="text"
                  value={formData?.byEmployee || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      byEmployee: e.target.value,
                    }))
                  }
                  className="form-control"
                  placeholder="Enter Employee Name"
                />
              </div>
            </div>
            <div className="mb-1">
              <label className="">Department</label>
              <div className="">
                <input
                  type="text"
                  value={formData?.department || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      department: e.target.value,
                    }))
                  }
                  className="form-control"
                  placeholder="Enter Department"
                />
              </div>
            </div>
            <div className="mb-1">
              <label className="">Amount</label>
              <div className="">
                <input
                  type="number"
                  value={formData?.amount || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      amount: e.target.value,
                    }))
                  }
                  className="form-control"
                  placeholder="Enter Amount"
                />
              </div>
            </div>
            <div className="mb-1">
              <label className="">Description</label>
              <div className="">
                <textarea
                  value={formData?.description || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  className="form-control"
                  rows={3}
                />
              </div>
            </div>

            <div className="mt-3">
              <div className="d-flex justify-content-end">
                <button
                  onClick={() => {
                    setFormData({});
                    setIsUpdateMode(false);
                    setShowAddModal(false);
                  }}
                  className="btn btn-secondary btn-sm"
                  type="button"
                >
                  Close
                </button>
                <button
                  disabled={loading}
                  className="btn btn-primary btn-sm ms-1"
                  type="submit"
                >
                  {isUpdateMode ? "Update" : "Add"}
                </button>
              </div>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      {/*   Export Modal
       ********************************************* */}
      <Modal show={showExportModal} onHide={() => setShowExportModal(false)}>
        <Modal.Body
          className="shadow-lg bg-white px-3"
          style={{ borderRadius: 15 }}
        >
          <div>
            <h4>Export Loss Run Report</h4>
          </div>
          <hr className="mt-0" />
          <form onSubmit={handleExportToExcel}>
            <div className="mb-3">
              <label className="form-label d-block mb-2">Quick Filters</label>
              <div className="d-flex flex-wrap gap-2 mb-3">
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => handleQuickFilter("today")}
                >
                  Today
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => handleQuickFilter("1week")}
                >
                  1 Week
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => handleQuickFilter("thisMonth")}
                >
                  This Month
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => handleQuickFilter("3months")}
                >
                  3 Months
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => handleQuickFilter("6months")}
                >
                  6 Months
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => handleQuickFilter("thisYear")}
                >
                  This Year
                </button>
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">Start Date</label>
              <input
                type="date"
                value={exportStartDate}
                onChange={(e) => setExportStartDate(e.target.value)}
                className="form-control"
                required
              />
            </div>
            <div className="mb-3">
              <label className="form-label">End Date</label>
              <input
                type="date"
                value={exportEndDate}
                onChange={(e) => setExportEndDate(e.target.value)}
                className="form-control"
                required
              />
            </div>
            <div className="d-flex justify-content-end">
              <button
                onClick={() => setShowExportModal(false)}
                className="btn btn-secondary btn-sm"
                type="button"
                disabled={exporting}
              >
                Cancel
              </button>
              <button
                className="btn btn-success btn-sm ms-1"
                type="submit"
                disabled={exporting}
              >
                {exporting ? "Exporting..." : "Export to Excel"}
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
}
